/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { createFeatureSelector, createSelector, createSelectorFactory, DefaultProjectorFn, resultMemoize, select } from '@ngrx/store';
import {
    analyticsHistoricalCallState,
    callFeature,
    CallState,
    localCallState,
    localHistoricalCallState,
    remoteCallState,
    remoteHistoricalCallState,
    selectAllAnalyticsHistoricalCalls,
    selectAllHistoricalCalls,
    selectAllLocalCalls,
    selectHistoricalRemoteCallEntities,
    selectLocalCallEntities,
    selectLocalHistoricalCallEntities,
    selectRemoteCallEntities
} from './call.reducer';
import {
    selectAcdIgnoredCalls,
    selectAgentStatus,
    selectAgentStatusRecord,
    selectAuthRecordClusterName,
    selectAutoLogoff,
    selectBidsMap,
    selectClusterLocalApplicationIsLoggedIn,
    selectClusterLocalApplicationPresence,
    selectHasPendingAcdAnswer,
    selectHasPendingWrapUp,
    selectIsLoggedIn,
    selectMemoizedUserAuthenticationRecord,
    selectMostRecentHandledCallIsAbandoned,
    selectMostRecentlyAnsweredAcdNenaCallId,
    selectPendingAcdAnswer,
    selectPendingBid,
    selectPosition,
    selectRequestedAgentStatus,
    selectSupervisedUserCallQueueNames,
    selectSupervisorFilteredAgents,
    selectUserCallQueueMap,
    selectUserCallQueueNames,
    selectUserMap,
    selectUsername,
    selectUsers
} from '../../user/+state/user.selectors';
import { CallFunctions } from '../util/call-functions';
import {
    selectAlertMediaOutputDeviceId,
    selectCtcAvailability,
    selectCtcMultipleWebsocketState,
    selectHasUserMedia,
    selectIsPhoneFullyRegistered,
    selectIsPhoneRegistered,
    selectMediaConnectionIsConnected,
    selectMediaConnectionsByClusterNameMap,
    selectUsbPeripheralReady,
    selectUserMediaOutputDeviceId,
    selectWebsocketConnectionState
} from './media.selectors';
import { SortFunctions } from '../util/sort-functions';
import { AgentStatus, AgentStatusRecord, Call, UserAuthenticationRecord } from 'CalltakingCoreApi';
import { pipe } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AcdFunctions } from '../../user/util/acd-functions';
import { BidResult } from '../../user/model/bid-result';
import { Dictionary } from '@ngrx/entity';
import { QueueDisplay } from '../../queue/queue-display-card/queue-display';
import { Bid } from '../../user/model/bid';
import {
    isQuiesced,
    selectCallAlertsMap,
    selectCallListTabEnabled,
    selectClusterConfigurationMap,
    selectCtcStatus,
    selectCtcStatusByName,
    selectHasInitialized,
    selectCallsDistributionPriority,
    selectPrimaryConnectionClusterName,
    selectQuiescence,
    selectUiConfiguration
} from '../../configuration/+state/configuration.selectors';
import {
    selectAbandonedCallsRoutingMode,
    selectActiveAgentTableFilters,
    selectActiveCallTableFilters,
    selectActiveRecentCallFilters,
    selectActiveStandaloneCallTableFilters,
    selectMaxQueueDisplayRows
} from '../../settings/+state/settings.selectors';
import { HistoricalCall } from '../model/historical-call';
import {
    selectAllStandardLocations,
    selectEnhancedLocationEvent,
    selectLocationDisplayTemplate_,
    selectServiceRespondersUrnMap,
    selectStandardLocationEvent,
    selectStandardLocationMap
} from '../../location/+state/location.selectors';
import { AgentTableData } from '../../supervisor/models/agent-table-data';
import { CallTableData } from '../../supervisor/models/call-table-data';
import {
    selectAlertOutputEnabled,
    selectAlertOutputToDevice,
    selectCCHubAttached,
    selectCCHubAttachedAndInitialized,
    selectCcHubAudioOutputConnected,
    selectCcHubCallAudioInputConnected,
    selectIsDeviceConnectedAndHeadsetDisconnected
} from './cchub.selectors';
import { holdComplete, releaseCallComplete } from './call.actions';
import { v4 } from 'uuid';
import { AdrSpeedDial } from '../../directory/model/adr-speed-dial';
import { DirectoryItemType } from '../../directory/model/directory-item';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { MediaFunctions } from '../util/media-functions';
import { QueueMetricObj } from '../../supervisor/queue-metrics/+state/metrics.model';
import { RequestedAgentStatus } from '../../user/model/requested-agent-status';
import { acdAnswerComplete, acknowledgeAbandonedComplete } from '../../user/+state/user.actions';
import { playAlertCondition } from '../../shared/alert.condition';
import { selectRtiAttached, selectSamAttached, selectUsbAlertOutputToDevice } from '../../usb/+state/usb.selectors';
import { ArrayFunctions } from '../../core/util/array-functions';
import { RxStompState } from '@stomp/rx-stomp';
import { ServiceResponderUrn } from '../../location/model/service-responders';
import { Location } from '../../location/model/location';
import { ConnectionState } from '../../core/model/connectionState';
import { LinkedCallData } from '../model/linked-call-data';
import { CallAlertFunctions } from '../util/call-alert-functions';
import { CallsDistributionMode, tableKey } from '../../settings/model/settings.model';

export const selectCallState = createFeatureSelector<CallState>(callFeature.name);
export const selectLocalCallState = createSelector(selectCallState, localCallState);
export const selectHistoricalLocalCallState = createSelector(selectCallState, localHistoricalCallState);
export const selectRemoteCallState = createSelector(selectCallState, remoteCallState);
export const selectHistoricalRemoteCallState = createSelector(selectCallState, remoteHistoricalCallState);
export const selectAnalyticsHistoricalCallState = createSelector(selectCallState, analyticsHistoricalCallState);

// Since array.filter returns a new object, any selector that filters for example a calls list, will emit when the input changes, regardless of whether the filtered results change
// In order to make our selectors smarter, we provide it with a custom memorization function to compare the contents of the array, thus the selectors utilizing array.filter can emit only when the result changes.

export const arrayFilterMemoize = (projectorFn: DefaultProjectorFn<Call[]>) => resultMemoize(projectorFn, arrayIsEqual);
export const callMemoize = (projectorFn: DefaultProjectorFn<Call>) => resultMemoize(projectorFn, callIsSame);

export function arrayIsEqual(a: any[], b: any[]): boolean {
    return a?.length === b?.length && a?.every((item) => b?.includes(item));
}

export function callIsSame(a: Call, b: Call): boolean {
    return a?.uuid === b?.uuid;
}

export const _selectCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectLocalCallState, selectAllLocalCalls);
export const selectCalls = createSelector(_selectCalls, selectMediaConnectionsByClusterNameMap, (calls, mediaConnectionsMap) =>
    calls.filter((call) => mediaConnectionsMap[call.clusterName]?.registered && !CallFunctions.isAbandonedDuplicated(call))
);

export const selectCallsByClusterName = (clusterName: string) => createSelector(_selectCalls, (calls: Call[]) => {
    return calls.filter((call) => call.clusterName === clusterName);
});

export const selectRingdownCalls = createSelectorFactory(arrayFilterMemoize)(selectCalls, (calls: Call[]) =>
    calls.filter((call) => call.ringdown && CallFunctions.isLive(call.status))
);
export const selectCallsMap = createSelector(selectLocalCallState, selectLocalCallEntities);
export const selectHistoricalCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectHistoricalLocalCallState, selectAllHistoricalCalls);
export const selectHistoricalCallsMap = createSelector(selectHistoricalLocalCallState, selectLocalHistoricalCallEntities);
export const selectRemoteCallsMap = createSelector(selectRemoteCallState, selectRemoteCallEntities);
export const selectHistoricalRemoteCallsMap = createSelector(selectHistoricalRemoteCallState, selectHistoricalRemoteCallEntities);

export const selectCall = (id: string) =>
    createSelector(selectCallsMap, selectHistoricalCallsMap, (callsMap, historicalCallsMap) => (callsMap[id] ? callsMap[id] : historicalCallsMap[id]));
export const getCall = (id: string) =>
    pipe(
        select(selectCall(id)),
        filter((val) => !!val),
        map((val) => val as Call)
    );

export const selectCallRedialUuid = (id: string) => createSelector(selectCall(id), (call) => call?.redialUUID);

export const selectLinkedCalls = (callId: string) => createSelector(selectCall(callId), (call) => call ? call.linkedCalls : []);

export const selectLinkedCallData = (callId: string) => createSelector(selectLinkedCalls(callId), selectCallsMap, selectHistoricalCallsMap, selectRemoteCallsMap, selectHistoricalRemoteCallsMap,
    (linkedCalls, callMap, historicalCallMap, remoteCallMap, historicalRemoteCallMap) => {
        return linkedCalls.map((linkedCall) =>
            ({
                ...linkedCall,
                call: callMap[linkedCall.linkedCallId] ? callMap[linkedCall.linkedCallId] :
                    historicalCallMap[linkedCall.linkedCallId] ? historicalCallMap[linkedCall.linkedCallId] :
                        remoteCallMap[linkedCall.linkedCallId] ? remoteCallMap[linkedCall.linkedCallId] :
                            historicalRemoteCallMap[linkedCall.linkedCallId]
            }) as LinkedCallData
        )
    });

export const selectLinkedCallIdBySourceRemoteParticipant = (localCallId: string, callId: string, remoteParticipantId: string) =>
    createSelector(selectLinkedCallData(localCallId), selectLinkedCall(callId), (linkedCallData, targetCall: Call) => CallFunctions.findLinkedCallBySourceRemoteParticipant(linkedCallData, targetCall, remoteParticipantId)?.linkedCallId);

export const selectLinkedCall = (linkedCallId: string) =>
    createSelector(selectCallsMap, selectHistoricalCallsMap, selectRemoteCallsMap, selectHistoricalRemoteCallsMap, (callMap, historicalCallMap, remoteCallMap, historicalRemoteCallMap) =>
        callMap[linkedCallId] ? callMap[linkedCallId] : historicalCallMap[linkedCallId] ? historicalCallMap[linkedCallId] : remoteCallMap[linkedCallId] ? remoteCallMap[linkedCallId] : historicalRemoteCallMap[linkedCallId]
    );

export const selectActiveLinkedCalls = (callId: string) =>
    createSelector(selectCall(callId), selectLinkedCallData(callId), (localCall, linkedCalls) =>
        localCall
            ? linkedCalls
                  .filter(({ call }) => !!call)
                  .filter(({ call }) => CallFunctions.isReleased(localCall.status) || CallFunctions.isActive(call.status))
                  .sort((a, b) => SortFunctions.oldestCreatedSort(a.call, b.call))
            : ([] as LinkedCallData[])
    );

export const selectNetworkParticipantsLinkedCallMap = (callId: string) =>
    createSelector(selectCall(callId), selectLinkedCallData(callId), (call, linkedCalls) => CallFunctions.mapNetworkParticipantsToLinkedCalls(call, linkedCalls));

export const selectLinkedCallIdByNetworkParticipantId = (callId: string, networkParticipantId: string) =>
    createSelector(selectNetworkParticipantsLinkedCallMap(callId), (map) => map[networkParticipantId]?.linkedCallId);

export const selectParticipantCount = (callId: string) =>
    createSelector(selectCall(callId), selectActiveLinkedCalls(callId), selectUsername, (call, linkedCalls, username: string) => {
        let networkParticipantCount = call && call.conferenceSnapshot ?
            call.conferenceSnapshot.participants.filter((cp) => !cp.leaveTime && !cp.agencyId).length : 0;
        return linkedCalls.reduce((accumulator, c) => accumulator + CallFunctions.participantCount(c.call, username as string, linkedCalls), networkParticipantCount);
    });

export const selectInboundRingingCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectCalls,
    selectMemoizedUserAuthenticationRecord,
    (calls: Call[], user: UserAuthenticationRecord) => calls.filter((call) => CallFunctions.isCallRingingUser(call, user))
);

export const selectAbandonedCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectCalls, (calls: Call[]) =>
    calls.filter((call) => CallFunctions.isAbandoned(call.status))
);

export const selectAbandonedCallsInQueues = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectAbandonedCalls,
    selectMemoizedUserAuthenticationRecord,
    (calls: Call[], user: UserAuthenticationRecord) => calls.filter((call) => CallFunctions.abandonedInUserQueues(call, user))
);

export const selectQueueDisplay = createSelector(
    selectUserCallQueueMap,
    selectInboundRingingCalls,
    selectAbandonedCallsInQueues,
    selectUsers, (callQueueMap, calls, abandonedCalls, agents) => {
    return Object.values(callQueueMap)
        .map(({ name, displayName }) => {
            // Get the earliest ringing call
            const inboundCalls = calls.filter((c) => {
                const isRinging = c.ringingCallQueues.includes(name);
                const isCascading = c.queueJoins[0]?.cascades?.includes(name);
                // Filter by calls that are ringing but not cascaded
                return isRinging && !isCascading;
            });

            const abandonedInQueue = abandonedCalls.filter((c) => {
                return c.abandonedQueueJoin && c.abandonedQueueJoin.leftOn == null ? c.abandonedQueueJoin.queueName === name : false;
            });

            const dgCalls =   inboundCalls.concat(abandonedInQueue).sort(SortFunctions.oldestCreatedSort);

            // Get the total active agents in the queue
            const dgAgents = agents.filter((a) => a.associatedCallQueues.includes(name) && !a.logoutDate);
            // Get the most recent queueJoin for this queue/call
            const latestQueueJoin =
                dgCalls[0] && CallFunctions.getCallQueueJoins(dgCalls[0])?.length
                    ? CallFunctions.getCallQueueJoins(dgCalls[0]).filter((q) => q.queueName === name)?.sort(SortFunctions.newestCreatedSort)[0]
                    : undefined;
            return {
                queue: displayName ?? name,
                calls: dgCalls.length,
                agents: dgAgents.length,
                idle: latestQueueJoin ? new Date(latestQueueJoin.createdTimestamp).getTime() : undefined
            } as QueueDisplay;
        })
        .sort((a, b) => (a.queue < b.queue ? -1 : b.queue > a.queue ? 1 : 0));
});

export const selectQueueDisplayCount = createSelector(selectMaxQueueDisplayRows, selectQueueDisplay, (maxRows, display) =>
    display.length > maxRows ? maxRows : display.length
);

export const selectAbandonedCallCount = createSelector(selectAbandonedCalls, (calls) => calls.length);

export const selectLiveCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectCalls, selectUsername, (calls: Call[], username: string) =>
    username === null ? [] : calls.filter((call) => CallFunctions.isLive(call.status))
);

export const selectCallsForSupervisorView = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectCalls, selectUsername, selectAbandonedCallsRoutingMode, (calls: Call[], username: string, routingMode: CallsDistributionMode) =>
    username === null ? [] : calls.filter((call) => routingMode === 'NO_ROUTING' ? CallFunctions.isLive(call.status) : !CallFunctions.isHistorical(call.status))
);

export const selectLiveSmsCallForNumber = (number: string) =>
    createSelector(selectLiveCalls, (calls) => calls.find((call) => call.text && call.callback.includes(number)));

export const selectOpenedCallIds = createSelector(selectCallState, (state) => state.openedCalls);
export const selectOpenedCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectOpenedCallIds,
    selectCallsMap,
    selectHistoricalCallsMap,
    (openedCallIds: string[], calls: Dictionary<Call>, historicalCalls: Dictionary<Call>) =>
        openedCallIds.map((callId) => (calls[callId] ? calls[callId] : historicalCalls[callId]))
);

export const selectLockedCallIds = createSelector(selectCallState, (state) => state.lockedCalls);

export const selectUnOpenedUnlockedHistoricalCalls = createSelector(
    selectHistoricalCalls,
    selectOpenedCallIds,
    selectLockedCallIds,
    (calls, openedCallIds, lockedCallIds) => calls.filter((call) => !openedCallIds.includes(call.uuid) && !lockedCallIds.includes(call.uuid))
);

export const selectObservationState = createSelector(selectCallState, (state) => state.observationState);
export const selectObservedUsername = createSelector(selectObservationState, (state) => state.observedUser);
export const selectIsObservationStateActive = createSelector(selectObservationState, (state) => state.state === 'active');

export const selectOpenCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectLiveCalls,
    selectObservedUsername,
    selectUsername,
    selectOpenedCalls,
    (liveCalls: Call[], observedUser: string, username: string, historicalCalls: Call[]) =>
        liveCalls
            .filter(
                (call) =>
                    CallFunctions.isActiveParticipant(call, username) ||
                    CallFunctions.isHeldParticipant(call, username) ||
                    CallFunctions.isActiveParticipant(call, observedUser) ||
                    CallFunctions.isHeldParticipant(call, observedUser)
            )
            .concat(historicalCalls)
);

export const selectMyCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectOpenCalls, selectUsername, (calls: Call[], username: string) =>
    username === null ? [] : calls.filter((call) => CallFunctions.isActiveParticipant(call, username) || CallFunctions.isHeldParticipant(call, username))
);

export const selectHasCall = createSelector(selectMyCalls, (calls) => Boolean(calls && calls.length));

export const selectMyActiveCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectLiveCalls,
    selectUsername,
    (calls: Call[], username: string) =>
        username === null ? [] : calls.filter((call) => CallFunctions.isActiveParticipant(call, username)).sort(SortFunctions.textLastSort)
);

export const selectAllActiveCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectLiveCalls, (calls: Call[]) =>
    calls.filter((call) => CallFunctions.hasActiveParticipants(call))
);

export const selectDiscrepancyReportCalls = createSelector(selectOpenCalls, selectHistoricalCalls, (myOpenCalls: Call[], historicalCalls: Call[]) => {
    return myOpenCalls.concat(historicalCalls.filter((historicalCall) => myOpenCalls.every((openCall) => historicalCall.uuid !== openCall.uuid)));
});

export const selectActiveCallId = createSelector(selectCallState, (state) => state.activeCallId);

export const selectActiveCall = createSelector(selectActiveCallId, selectCallsMap, (activeCallId, callsMap) =>
    activeCallId ? callsMap[activeCallId] : undefined
);

export const selectSelectedCallId = createSelector(selectCallState, (state) => state.selectedCallId);

export const selectSelectedCall = createSelector(
    selectCallsMap,
    selectHistoricalCallsMap,
    selectSelectedCallId,
    (callsMap, historicalCallsMap, selectedCallId) =>
        selectedCallId ? (callsMap[selectedCallId] ? callsMap[selectedCallId] : historicalCallsMap[selectedCallId]) : null
);

export const selectSelectedCallRedialUuid = createSelector(selectSelectedCall, (call) => call?.redialUUID);

export const selectHasSelectedCall = createSelector(selectSelectedCall, (call) => Boolean(call));

export const selectSelectedCallIsCama = createSelector(selectSelectedCall, (call) => call && call?.cama);

export const selectSelectedCallIsFlashEnabled = createSelector(selectSelectedCall, (call) => call && (call.cama || call.analogAdmin));

export const selectActiveOrSelectedCall = createSelector(selectActiveCall, selectSelectedCall, (activeCall, selectedCall) => activeCall || selectedCall);

export const selectPendingReleasedCall = createSelector(selectCallState, (state) => state.pendingReleasedCall);

export const selectPendingHeldCall = createSelector(selectCallState, (state) => state.pendingHeldCall);

export const selectPendingHeldCallCallback = createSelector(selectCallState, (state) => state.heldCallCallback);

export const selectHoldComplete = createSelector(
    selectCallsMap,
    selectUsername,
    selectPendingHeldCall,
    selectPendingHeldCallCallback,
    (callsMap, username, pendingHeldCall, callback) =>
        pendingHeldCall && CallFunctions.isHeld(callsMap[pendingHeldCall] as Call, username)
            ? holdComplete({ callId: pendingHeldCall, callback: callback })
            : undefined
);

export const selectReleaseSuccess = createSelector(selectCallsMap, selectUsername, selectPendingReleasedCall, (callsMap, username, pendingReleasedCall) =>
    pendingReleasedCall && CallFunctions.hasUserReleased(callsMap[pendingReleasedCall] as Call, username)
        ? releaseCallComplete({ callId: pendingReleasedCall })
        : undefined
);

export const selectACDAnswerSuccess = createSelector(
    selectCallsMap,
    selectUsername,
    selectPendingAcdAnswer,
    selectMediaConnectionsByClusterNameMap,
    (callsMap, username, pendingAcdAnswer, mediaConnectionsMap) =>
        pendingAcdAnswer && CallFunctions.isActiveParticipant(callsMap[pendingAcdAnswer.callId], username)
            ? acdAnswerComplete({ callId: pendingAcdAnswer.callId, nenaId: pendingAcdAnswer.nenaId, clusterName: pendingAcdAnswer.clusterName })
            : undefined
);

export const selectComputeActiveCallId = createSelector(
    selectActiveCallId,
    selectMyActiveCalls,
    selectSelectedCallId,
    (activeCallId, availableActiveCalls, selectedCallId) => {
        let currentActiveCallIsValid = Boolean(activeCallId && availableActiveCalls.map((call) => call.uuid).includes(activeCallId));
        let selectedCallOverride = Boolean(selectedCallId && availableActiveCalls.map((call) => call.uuid).includes(selectedCallId));
        return selectedCallOverride ? selectedCallId : currentActiveCallIsValid ? activeCallId : availableActiveCalls[0]?.uuid;
    }
);

export const selectPendingAcknowledgeAbandonedCallId = createSelector(selectCallState, (state: CallState) => state.pendingAcknowledgedAbandonedCallId);

export const selectAcknowledgeAbandonedCallComplete = createSelector(
    selectUsername,
    selectCallsMap,
    selectPendingAcknowledgeAbandonedCallId,
    (username: string, callsMap, pendingAcknowledgedAbandonedCallId) =>
        pendingAcknowledgedAbandonedCallId && callsMap[pendingAcknowledgedAbandonedCallId].abandonedAcknowledgedBy === username ?
            acknowledgeAbandonedComplete({ callId: pendingAcknowledgedAbandonedCallId, clusterName: callsMap[pendingAcknowledgedAbandonedCallId].clusterName })
            : undefined
);

export const selectAbandonedAcknowledgeCallId = createSelector(selectCallState, (state: CallState) => state.acknowledgedAbandonedCallId);
export const selectHasAcknowledgedAbandonedCall = createSelector(selectAbandonedAcknowledgeCallId, (callId: string) => Boolean(callId));

export const selectActiveVoiceCall = createSelector(selectMyActiveCalls, (calls: Call[]) => calls.find((call) => !call.text));

export const selectHasActiveInboundVoiceCall = createSelector(selectActiveVoiceCall, (call: Call) => Boolean(call?.type === 'INBOUND'));

export const selectHasActiveVoiceCall = createSelector(selectActiveVoiceCall, (call) => Boolean(call));

// Consider calls I'm marked as the acdAssignedAgent AND calls I have a won bid on as assigned since we may get the bid result before the call is marked as assigned
export const selectAssignedCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectInboundRingingCalls,
    selectUsername,
    selectBidsMap,
    (ringingCalls: Call[], username: string, bidsMap: Dictionary<BidResult>) =>
        username ? ringingCalls.filter((call) => call.acdAssignedAgent === username || bidsMap[call.uuid]?.result === 'WON') : []
);

// includes previously queued acd calls that are no longer in an acd queue (aka i'm being conference in on someone's acd delivered call or the calls put into a ring all queue)
export const selectNonAcdCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(selectInboundRingingCalls, selectUsername, (ringingCalls: Call[]) =>
    ringingCalls.filter((call) => !call.acdAssignedAgent && CallFunctions.getCallQueueJoins(call).every((queueJoin) => !queueJoin.acd || (queueJoin.acd && queueJoin.leftOn)))
);

export const selectMyInboundCalls = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectAssignedCalls,
    selectNonAcdCalls,
    selectCallsDistributionPriority,
    (assignedCalls: Call[], nonAssignedCalls: Call[], callsDistributionPriority: string) => {
        if (callsDistributionPriority === 'INCOMING_CALLS') {
            return assignedCalls.concat(nonAssignedCalls.sort(SortFunctions.incomingFirstSort));
        } else if (callsDistributionPriority === 'ABANDONED_CALLS') {
            return assignedCalls.concat(nonAssignedCalls.sort(SortFunctions.abandonedFirstSort));
        } else {
            return assignedCalls.concat(nonAssignedCalls);
        }
    }
);

export const selectAssignedCall = createSelector(selectAssignedCalls, (assignedCalls) => assignedCalls[0]);

export const selectNextCallToAnswer = createSelector(
    selectAssignedCalls,
    selectNonAcdCalls,
    (assignedCalls, ringingNonAcdCalls) => assignedCalls.concat(ringingNonAcdCalls)[0]
);

/* ******************* ACD ******************* */

export const selectHasAnyActiveAcdCalls = createSelector(selectMyCalls, selectUsername, (calls: Call[], username: string | undefined) =>
    Boolean(username && calls.some((call) => call.acd && !call.participants[username].leftOn))
);

export const selectHasAnyActiveNonAcdCalls = createSelector(selectMyCalls, selectUsername, (calls: Call[], username: string | undefined) =>
    Boolean(username && calls.some((call) => !call.acd && !call.participants[username].leftOn))
);

export const selectHasAnyHeldNonIgnoredCalls = createSelector(selectMyCalls, selectAcdIgnoredCalls, (calls: Call[], acdIgnoredCalls: {}) =>
    // @ts-ignore
    calls.some((call) => call.status === 'ON_HOLD' && !acdIgnoredCalls[call.uuid])
);

export const selectHasAnyHeldCalls = createSelector(selectMyCalls, (calls: Call[]) => calls.some((call) => call.status === 'ON_HOLD'));

export const selectHasAssignedCall = createSelector(selectAssignedCall, (call: Call) => Boolean(call));

export const selectHasAbandonedAssignedCall = createSelector(selectAssignedCall, (call: Call) =>
    Boolean(call && CallFunctions.isAbandoned(call.status))
);

export const selectHasRingingAssignedCall = createSelector(selectAssignedCall, (call: Call) =>
    Boolean(call && !CallFunctions.isAbandoned(call.status))
);

export const selectHasRepresentedCallAssigned = createSelector(selectAssignedCall, selectMostRecentlyAnsweredAcdNenaCallId, (call: Call, previousNenaId) =>
    Boolean(call && previousNenaId && call.nenaCallId === previousNenaId)
);

export const statusMemoize = (projectorFn: DefaultProjectorFn<RequestedAgentStatus>) => resultMemoize(projectorFn, statusIsSame);

export function statusIsSame(a: RequestedAgentStatus, b: RequestedAgentStatus): boolean {
    return a?.status === b?.status && a?.isAgentRequested === b?.isAgentRequested
        && a?.statusReasonDisplayName === b?.statusReasonDisplayName && a?.clusterName === b?.clusterName;
}

export const selectComputedAgentStatus = createSelectorFactory<object, RequestedAgentStatus>(statusMemoize)(
    selectRequestedAgentStatus,
    selectAgentStatus,
    selectIsPhoneRegistered,
    selectHasRingingAssignedCall,
    selectHasAbandonedAssignedCall,
    selectHasAnyActiveNonAcdCalls,
    selectHasAnyHeldNonIgnoredCalls,
    selectHasAnyActiveAcdCalls,
    selectHasAcknowledgedAbandonedCall,
    selectHasPendingWrapUp,
    selectHasPendingAcdAnswer,
    selectAuthRecordClusterName,
    selectHasRepresentedCallAssigned,
    (
        requestedStatus: RequestedAgentStatus,
        agentStatus: AgentStatus,
        phoneRegistered: boolean,
        hasRingingAssignedCall: boolean,
        hasAbandonedAssignedCall: boolean,
        anyActiveNonAcdCalls: boolean,
        anyHeldNonIgnoredCalls: boolean,
        anyActiveAcdCalls: boolean,
        hasAbandonedAcknowledgedCall: boolean,
        pendingWrapUp: boolean,
        hasPendingAcdAnswer: boolean,
        primaryCtc: string,
        hasRepresentedCallAssigned: boolean
    ) =>
        primaryCtc && !hasPendingAcdAnswer
            ? AcdFunctions.computeStatus(
                  requestedStatus,
                  agentStatus,
                  phoneRegistered,
                  hasRingingAssignedCall,
                  hasAbandonedAssignedCall,
                  anyActiveNonAcdCalls,
                  anyHeldNonIgnoredCalls,
                  anyActiveAcdCalls,
                  pendingWrapUp,
                  hasAbandonedAcknowledgedCall,
                  hasRepresentedCallAssigned,
                  primaryCtc
              )
            : undefined
);

export const selectAssignedWonBid = createSelector(selectBidsMap, selectAssignedCall, selectAgentStatus, (bidsMap, assignedCall, agentStatus) => {
    return assignedCall && (agentStatus === 'ASSIGNED_CALL' || agentStatus === 'ASSIGNED_ABANDONED_CALL') && bidsMap[assignedCall.uuid] ? bidsMap[assignedCall.uuid] : undefined
    }
);

export const selectAutoAnswerAssignedWonBid = createSelector(selectAssignedWonBid, selectUserCallQueueMap, (assignedWonBid, acdQueueMap) => {
    let autoAnswer = assignedWonBid?.previouslyAssigned || (assignedWonBid && acdQueueMap[assignedWonBid.queueName]?.queueACD.answerMethod.autoAnswer);
    return assignedWonBid && autoAnswer ? assignedWonBid : undefined;
});

export const selectCallsToReject = createSelectorFactory<object, Call[]>(arrayFilterMemoize)(
    selectAssignedCalls,
    selectAgentStatusRecord,
    (assignedCalls: Call[], statusRecord: AgentStatusRecord) =>
        statusRecord?.status === 'NOT_READY' && statusRecord?.agentRequestedStatus ? assignedCalls : []
);

export const selectBids = createSelector(
    selectInboundRingingCalls,
    selectBidsMap,
    selectMostRecentlyAnsweredAcdNenaCallId,
    selectMostRecentHandledCallIsAbandoned,
    selectCallsDistributionPriority,
    (ringingCalls, bidsMap, previousNenaId, mostRecentHandledCallIsAbandoned, callsDistributionPriority) => {
        const incomingFirst = callsDistributionPriority === 'INCOMING_CALLS' || (callsDistributionPriority === 'ALTERNATING' && mostRecentHandledCallIsAbandoned);
        return CallFunctions.createBids(ringingCalls, bidsMap, previousNenaId, incomingFirst);
    }
);

const selectNextBid_ = createSelector(
    selectIsDeviceConnectedAndHeadsetDisconnected,
    selectHasAssignedCall,
    selectPendingBid,
    selectAgentStatusRecord,
    selectUserCallQueueMap,
    selectBids,
    (headsetDisconnected, assignedCall, pendingBid, agentStatus, callQueueMap, availableBids) => {
        let bid: Bid | undefined;
        if (!headsetDisconnected && !assignedCall && !pendingBid && agentStatus && availableBids.length) {
            bid = availableBids[0].previouslyAssigned
                ? availableBids[0]
                : agentStatus.status === 'READY'
                  ? availableBids[0]
                  : agentStatus.status === 'ON_CALL'
                    ? availableBids.find((bid) => !bid.queueJoin.autoAnswer && callQueueMap[bid.queueJoin.queueName].queueACD.prioritizeRingOne)
                    : undefined;
            if (bid) {
                bid.selectionTime = new Date(agentStatus.date).getTime();
            }
        }
        return bid;
    }
);

export const selectNextBid = pipe(
    select(selectNextBid_),
    filter((val) => !!val),
    map((val) => val as Bid)
);

/* ******************* Supervisor View ******************* */

export const selectAgentTableData = createSelector(
    selectAllActiveCalls,
    selectSupervisorFilteredAgents,
    selectActiveAgentTableFilters,
    selectClusterConfigurationMap,
    (calls, agents, filters, clusterMap) =>
        agents
            .filter((agent) => !(filters.includes(agent.agentStatus) || filters.includes(agent.preferredRole?.toLowerCase())))
            .map((agent) => {
                let call = calls.find((call) => CallFunctions.isActiveParticipant(call, agent.username));
                let agentCalls = calls.filter(
                    (call) => CallFunctions.isActiveParticipant(call, agent.username) || CallFunctions.isHeldParticipant(call, agent.username)
                ).length;
                let status = call ? CallFunctions.getCallStatus(call, agent) : undefined;
                let callDuration = call ? call.createdTimestamp : 0;
                let joinedTime = call ? call.participants[agent.username].joinedOn : 0;
                let queue = call
                    ? [...call.queueJoins]
                          ?.sort(SortFunctions.joinedOnSort)
                          .reverse()
                          .map((queue) => queue.queueName)[0]
                    : undefined;
                return {
                    ...agent,
                    callCount: agentCalls,
                    priorityCallStatus: status,
                    priorityCallDuration: callDuration,
                    priorityCallAgentJoinedOn: joinedTime,
                    priorityQueueName: queue,
                    clusterLabel: clusterMap[agent.clusterName].clusterLabel
                } as AgentTableData;
            })
);

export const selectMyRecentLiveCalls = createSelector(selectLiveCalls, selectUsername, (calls, username) =>
    calls.filter((call) => CallFunctions.isInactiveParticipant(call, username) && !CallFunctions.isHeld(call, username))
);

// recent calls may contain records for other agents using the same position
export const selectAllRecentCalls = createSelector(
  selectMyRecentLiveCalls,
  selectHistoricalCalls,
  selectUsername,
  selectPosition,
  (live, historical, username, position) =>
    [...live, ...historical].sort((a, b) => SortFunctions.newestLeftOnSort(a, b, username, position))
);

export const selectSlicedRecentCalls = createSelector(
    selectAllRecentCalls,
    selectLockedCallIds,
    selectUiConfiguration,
    (recentCalls, lockedCallIds, { recentCallLimit }) => {
        // get the 'n' most recent calls regardless if they are locked or not
        const limitedRecentCalls = recentCalls.slice(0, recentCallLimit);
        // add in any locked calls not already a part of the limited list
        const lockedCallsNotPartOfLimitedRecentCalls = recentCalls.filter((c) => lockedCallIds.includes(c.uuid))
            .filter((lockedCall) => !limitedRecentCalls.map((c) => c.uuid).includes(lockedCall.uuid));
        return limitedRecentCalls.concat(lockedCallsNotPartOfLimitedRecentCalls);
    }
);

export const selectRecentCallCount = createSelector(selectSlicedRecentCalls, (calls) => calls.length);

export const selectFilteredRecentCalls = createSelector(
    selectSlicedRecentCalls,
    selectActiveRecentCallFilters,
    selectLockedCallIds,
    (recentCalls, filters, lockedCallIds) => CallFunctions.filterRecentCalls(recentCalls, filters, lockedCallIds)
);

export const selectCallTableCalls = createSelector(selectLiveCalls, selectUserCallQueueNames, (calls: Call[], userCallQueues) =>
    calls.filter((call) => userCallQueues.some((queue) => call.callQueues.includes(queue)))
);

export const selectSupervisorCallTableCalls = createSelector(selectCallsForSupervisorView, selectSupervisedUserCallQueueNames, (calls: Call[], supervisedCallQueues) =>
    calls.filter((call) => supervisedCallQueues.some((queue) => call.callQueues.includes(queue)))
);

export const selectSupervisorFilteredCalls = createSelector(
    selectSupervisorCallTableCalls,
    selectActiveCallTableFilters,
    (calls, callFilters) => CallFunctions.filterCallsByTableFilter(calls, callFilters) as CallTableData[]
);

export const selectSupervisorCallsConnected = createSelector(selectSupervisorFilteredCalls, (calls) =>
    calls.filter((c) => CallFunctions.isConnected(c.status))
);
export const selectSupervisorCallsConnectedCount = createSelector(selectSupervisorCallsConnected, (calls) => calls.length);
export const selectSupervisorLongestCallConnected = createSelector(selectSupervisorCallsConnected, (calls) => CallFunctions.getOldestTimestamp(calls));
export const selectSupervisorCallsRinging = createSelector(selectSupervisorFilteredCalls, (calls) => calls.filter((c) => CallFunctions.isRinging(c)));
export const selectSupervisorCallsRingingCount = createSelector(selectSupervisorCallsRinging, (calls) => calls.length);
export const selectSupervisorLongestCallRinging = createSelector(selectSupervisorCallsRinging, (calls) => CallFunctions.getOldestTimestamp(calls));
export const selectSupervisorCallsHeld = createSelector(selectSupervisorFilteredCalls, (calls) => calls.filter((c) => CallFunctions.isHeld(c)));
export const selectSupervisorCallsHeldCount = createSelector(selectSupervisorCallsHeld, (calls) => calls.length);
export const selectSupervisorLongestCallHeld = createSelector(selectSupervisorCallsHeld, (calls) => CallFunctions.getOldestTimestamp(calls));

export const selectSupervisorCallSummaryData = createSelector(
    selectSupervisorCallsConnectedCount,
    selectSupervisorLongestCallConnected,
    selectSupervisorCallsRingingCount,
    selectSupervisorLongestCallRinging,
    selectSupervisorCallsHeldCount,
    selectSupervisorLongestCallHeld,
    (callsConnected, longestCallConnected, callsRinging, longestCallRinging, callsOnHold, longestCallOnHold) => {
        return {
            callsConnected,
            longestCallConnected,
            callsRinging,
            longestCallRinging,
            callsOnHold,
            longestCallOnHold
        } as Partial<QueueMetricObj>;
    }
);

export const selectFilteredCallSummaryAsList = createSelector(
    selectSupervisorCallSummaryData,
    (summary) => [{ uuid: 'SUMMARY_QUEUES' as tableKey, ...summary }] as QueueMetricObj[]
);

export const selectSupervisorScopedCallCount = createSelector(selectSupervisorCallTableCalls, (calls) => calls.length);
export const selectSupervisorFilteredCallCount = createSelector(selectSupervisorFilteredCalls, (calls) => calls.length);

export const selectFilteredCallTableCalls = createSelector(
    selectCallTableCalls,
    selectActiveStandaloneCallTableFilters,
    (calls, filters) => CallFunctions.filterCallsByTableFilter(calls, filters) as CallTableData[]
);

export const selectIsObserving = createSelector(selectObservedUsername, (observedUsername) => Boolean(observedUsername));

export const selectObservedUser = createSelector(selectUserMap, selectObservedUsername, (users, observedUsername) => users[observedUsername]);

export const selectObservedUserCalls = createSelector(selectObservedUsername, selectLiveCalls, (observedUsername, calls) =>
    !observedUsername
        ? []
        : calls
              .filter((c) => CallFunctions.isParticipant(c, observedUsername) || CallFunctions.isHeldParticipant(c, observedUsername))
              .sort(SortFunctions.textFirstSort)
);

export const selectObservedUserCallCount = createSelector(
    selectObservedUserCalls,
    selectObservedUsername,
    (observedCalls, observedUsername) =>
        observedCalls.filter((c) => CallFunctions.isActiveParticipant(c, observedUsername) || CallFunctions.isHeldParticipant(c, observedUsername)).length
);

export const selectObservedUserHasCall = createSelector(selectObservedUserCallCount, (count) => Boolean(count));

export const selectNextCallToObserve = createSelectorFactory<object, Call>(callMemoize)(
    selectIsObservationStateActive,
    selectActiveVoiceCall,
    selectUsername,
    selectObservedUsername,
    selectObservedUserCalls,
    (isObservationStateActive: boolean, activeVoiceCall: Call, username: string, observedUsername: string, observedUserCalls: Call[]) =>
        !isObservationStateActive
            ? undefined
            : observedUserCalls.find(
                  (call) =>
                      (CallFunctions.isActiveParticipant(activeVoiceCall, username) && !CallFunctions.isSilentMonitoring(activeVoiceCall, username)) ||
                      (!CallFunctions.isActiveParticipant(call, username) &&
                          !CallFunctions.isSilentMonitoring(call, username) &&
                          CallFunctions.isConnected(call.status) &&
                          CallFunctions.isActiveParticipant(call, observedUsername))
              )
);

export const selectNextObservedCallToRelease = createSelector(
    selectUsername,
    selectObservedUsername,
    selectObservedUserCalls,
    (username, observedUsername, calls) =>
        calls.find(
            (call) =>
                CallFunctions.isSilentMonitoring(call, username) &&
                CallFunctions.isInactiveParticipant(call, observedUsername) &&
                !CallFunctions.isHeld(call, observedUsername)
        )
);

export const selectSelectedObservedCall = createSelector(selectSelectedCallId, selectObservedUserCalls, (selectedCallId, observedCalls) =>
    observedCalls.find((call) => call.uuid === selectedCallId)
);

export const selectIsDirectoryDisabled = createSelector(
    selectSelectedCall,
    selectUsername,
    selectIsPhoneRegistered,
    (call, username, registered) =>
        !registered || (call && (!CallFunctions.isActiveParticipant(call, username) || CallFunctions.isSilentMonitoring(call, username)))
);

export const selectAutoLogoffInitiated = createSelector(selectAutoLogoff, selectMyActiveCalls, (autoLogoffRequest, calls) =>
    autoLogoffRequest && autoLogoffRequest.forcedLogout
        ? autoLogoffRequest
        : calls.length ? undefined : autoLogoffRequest
);

export const selectLogoffDisabled = createSelector(selectMyActiveCalls, selectHasAnyHeldCalls, selectCallListTabEnabled,
    (calls, hasHeldCall, callsListEnabled) => Boolean(calls.length) || (hasHeldCall && !callsListEnabled));

export const selectAllAnalyticsCallHistory = createSelectorFactory<object, HistoricalCall[]>(arrayFilterMemoize)(
    selectAnalyticsHistoricalCallState,
    selectAllAnalyticsHistoricalCalls
);
export const selectAnalyticsCallHistory = (referenceKey: string) =>
    createSelector(selectAllAnalyticsCallHistory, selectLocationDisplayTemplate_, (history, template) =>
        history.filter((record) => record.referenceKey === referenceKey)
    );

export const selectSelectedCallStandardLocationId = createSelector(
    selectSelectedCallId,
    selectSelectedCallRedialUuid,
    selectAllStandardLocations,
    (callId, redialUuid, locations) => locations.find((locationEvent) => locationEvent.callId === callId || locationEvent.callId === redialUuid)?.locationId
);

export const selectSelectedCallStandardLocationEvent = createSelector(
    selectSelectedCallStandardLocationId,
    selectStandardLocationMap,
    (locationId, locationMap) => locationMap[locationId]
);

export const selectStandardLocationEsn = createSelector(selectSelectedCallStandardLocationEvent, (locationEvent) => locationEvent?.camaEsn);

export const selectSelectedCallStandardLocation = createSelector(selectSelectedCallStandardLocationEvent, (locationEvent) => locationEvent?.location);

export const adrArrayMemoize = (projectorFn: DefaultProjectorFn<AdrSpeedDial[]>) =>
    resultMemoize(
        projectorFn,
        (a: AdrSpeedDial[], b: AdrSpeedDial[]) => a?.length === b?.length && a?.map((adr) => adr.uri).every((uri) => b?.map((adr) => adr.uri).includes(uri))
    );

export const selectStandardLocationAdrSpeedDials = createSelectorFactory<object, AdrSpeedDial[]>(adrArrayMemoize)(
    selectSelectedCallStandardLocation,
    selectServiceRespondersUrnMap,
    (location: Location, serviceRespondersMap: Record<string, ServiceResponderUrn>) => {
        return location?.emergencyCallDataRefs
            ? location.emergencyCallDataRefs
                  .map((emergencyCallDataRef) =>
                      emergencyCallDataRef.uris
                          ? emergencyCallDataRef.uris
                                .filter((uri) => MediaFunctions.isSipUri(uri) || isValidPhoneNumber(uri, 'US'))
                                .map((uri) => {
                                    let serviceResponder = serviceRespondersMap[emergencyCallDataRef.ref];
                                    return {
                                        uuid: v4(),
                                        type: DirectoryItemType.ADR_BUTTON,
                                        name: `${emergencyCallDataRef.type} ${emergencyCallDataRef.purpose}`,
                                        color: serviceResponder ? serviceResponder.color : 'grey',
                                        uri: uri,
                                        icon: serviceResponder ? serviceResponder.icon : 'ic_status_unknown'
                                    } as AdrSpeedDial;
                                })
                          : []
                  )
                  .flat()
            : [];
    }
);

export const selectRebidDisabled = (callId: string) =>
    createSelector(selectCallsMap, (callMap) =>
        Boolean(callMap[callId]?.type === 'OUTBOUND' || callMap[callId]?.type === 'INTERNAL' || callMap[callId]?.ringdown)
    );

export const selectReportDisabled = (callId: string) =>
    createSelector(selectRebidDisabled(callId), (rebidDisabled) => {
        return rebidDisabled;
    });

export const selectCopyDisabled = (callId: string) =>
    createSelector(
        selectRebidDisabled(callId),
        selectStandardLocationEvent(callId),
        selectEnhancedLocationEvent(callId),
        (rebidDisabled, standardLocation, enhancedLocation) => {
            return rebidDisabled || !(standardLocation || enhancedLocation);
        }
    );

/** CCHub call state dependant selectors **/

const _selectCcHubOnCallState = createSelector(selectHasActiveVoiceCall, selectCCHubAttachedAndInitialized, (onCall, ccHubOn) =>
    ccHubOn ? onCall : undefined
);

export const selectCcHubHeadsetMuteControlsDisabled = createSelector(_selectCcHubOnCallState, (onCall) => !Boolean(onCall));

export const selectLlrState = pipe(
    select(_selectCcHubOnCallState),
    filter((val) => val !== undefined),
    map((val) => val)
);

export const selectOffHookState = pipe(
    select(_selectCcHubOnCallState),
    filter((val) => val !== undefined),
    map((val) => val)
);

export const selectBargedIn = (callId: string) =>
    createSelector(
        selectCallsMap,
        selectUsername,
        selectObservationState,
        (callMap, username: string, state) => state.bargedIn === callId && CallFunctions.isActiveParticipant(callMap[callId], username)
    );

export const selectCcHubInputOnCall = (peripheral: string) =>
    createSelector(selectCcHubCallAudioInputConnected(peripheral), _selectCcHubOnCallState, (connected, onCall) => {
        return connected && onCall;
    });

export const selectCcHubOutputOnCall = (peripheral: string) =>
    createSelector(selectCcHubAudioOutputConnected(peripheral), _selectCcHubOnCallState, (connected, onCall) => {
        return connected && onCall;
    });

export const selectHoldDisabled = createSelector(selectCallState, (state) => Boolean(state.pendingUnHold || state.pendingHeldCall));

export const selectIsExternalAlertDeviceConfigured = createSelector(
    selectCCHubAttached,
    selectAlertOutputToDevice,
    selectRtiAttached,
    selectSamAttached,
    selectUsbAlertOutputToDevice,
    (ccHubConnected, ccHubAlertOutputToDevice, rtiAttached, samAttached, usbAlertOutputToDevice) =>
        (ccHubConnected && ccHubAlertOutputToDevice) || ((rtiAttached || samAttached) && usbAlertOutputToDevice)
);
export const selectDefaultAudioServiceAlertingEnabled = createSelector(
    selectUserMediaOutputDeviceId,
    selectAlertMediaOutputDeviceId,
    selectIsObservationStateActive,
    selectHasActiveVoiceCall,
    selectIsExternalAlertDeviceConfigured,
    (userMediaOutputDeviceId, alertMediaOutputDeviceId, isObservationStateActive, hasActiveVoiceCall, isExternalAlertDevice) =>
        playAlertCondition(userMediaOutputDeviceId, alertMediaOutputDeviceId, isObservationStateActive, hasActiveVoiceCall, isExternalAlertDevice)
);

export const selectCcHubAlertOutputEnabled = (peripheral: string) =>
    createSelector(
        selectIsObservationStateActive,
        selectHasActiveVoiceCall,
        selectAlertOutputEnabled(peripheral),
        (isObserving, hasActiveVoiceCall, alertOutputEnabled) => alertOutputEnabled && (!hasActiveVoiceCall || isObserving)
    );

export const selectHeadsetAlertingEnabled = createSelector(
    selectIsObservationStateActive,
    selectHasActiveVoiceCall,
    (isObserving, hasActiveVoiceCall) => !hasActiveVoiceCall || isObserving
);

export const selectCallRack = (callId: string) =>
    createSelector(selectCallsMap, selectClusterConfigurationMap, (callsMap, clusterMap) => {
        return clusterMap[callsMap[callId].clusterName]?.rackId;
    });

export const isQuiescedAndOnCall = createSelector(
    selectHasInitialized,
    isQuiesced,
    selectHasCall,
    (hasInitialized: boolean, quiesced: boolean, hasCall: boolean) => ({
        hasInitialized,
        quiesced,
        hasCall
    })
);

export const selectCallStateMultipleSubscriptions = createSelector(selectCallState, (state) => state.multipleWebsocketSubscriptions);
export const selectAllMultipleCallStateSubscriptionsConfirmed = (clusterName: string) => createSelector(selectCallStateMultipleSubscriptions, (subscriptions) =>
    subscriptions[clusterName] ? Object.values(subscriptions[clusterName])
        .every((confirmed) => confirmed) : false);
export const selectMultipleCallStateInitializationTime = (clusterName: string) =>
    createSelector(
        selectAllMultipleCallStateSubscriptionsConfirmed(clusterName),
        selectClusterLocalApplicationPresence(clusterName),
        selectCtcStatusByName(clusterName),
        (subscribed, presence, ctcAvailable) => Boolean(ctcAvailable && subscribed && presence?.loggedIn && presence?.loginTime) ? presence.loginTime : 0);


export const selectPrimaryActiveClusterNameMultipleWebsocketConnection = createSelector(
    selectCtcMultipleWebsocketState,
    selectQuiescence,
    selectCtcStatus,
    selectPrimaryConnectionClusterName,
    selectActiveCall,
    (ctcState, quiescence, ctcStatus, primaryClusterName, call) => {
        if(!!call && (call?.clusterName === primaryClusterName && quiescence[primaryClusterName]) && ctcStatus[primaryClusterName]) {
            return primaryClusterName;
        }
        let availableClusterNames = Object.entries(ctcState).filter(([key, value]) => {
            return ctcStatus[key] && !quiescence[key] && value.connectionStatus === RxStompState.OPEN;
        });
        return primaryClusterName && availableClusterNames.some(([key]) => key === primaryClusterName) ? primaryClusterName : availableClusterNames.length === 0 ? primaryClusterName : ArrayFunctions.selectRandom(availableClusterNames)[0];
    });


export const selectHasWebsocketConnection = (clusterName: string, primary?: boolean) => createSelector(
    selectWebsocketConnectionState(clusterName, primary),
    selectActiveCall,
    selectCtcMultipleWebsocketState,
    (state, activeCall, multipleWebsocketState) => activeCall ? Boolean(state === RxStompState.OPEN && activeCall && multipleWebsocketState[activeCall.clusterName].connectionStatus === RxStompState.OPEN) : state === RxStompState.OPEN);

export const selectWebsocketDisconnected = createSelector(selectHasWebsocketConnection(null, true), selectHasInitialized, selectActiveCall, selectCtcMultipleWebsocketState, (hasConnection, hasInitialized, activeCall, multipleWebsocketState) => {
    return Boolean(hasInitialized && !hasConnection || activeCall && multipleWebsocketState[activeCall.clusterName].connectionStatus !== RxStompState.OPEN);
});

export const selectConnectionStatus = createSelector(
    selectPosition,
    selectHasUserMedia,
    selectHasWebsocketConnection(null, true),
    selectIsPhoneRegistered,
    selectIsPhoneFullyRegistered,
    selectUsbPeripheralReady,
    isQuiesced,
    selectIsLoggedIn,
    (hasPosition, hasMic, hasWebsocket, hasPhone, hasAllPhones, usbDeviceReady, isQuiesced, loggedIn) =>
        loggedIn && hasPosition && hasMic && hasWebsocket && hasAllPhones && usbDeviceReady && !isQuiesced
            ? ConnectionState.FULL
            : hasPosition && hasMic && hasWebsocket && hasPhone && usbDeviceReady
                ? ConnectionState.PARTIAL
                : ConnectionState.DOWN
);

export const selectIsNetworkDisconnected = createSelector(selectHasInitialized, selectConnectionStatus, (initialized, status) => initialized && status === ConnectionState.DOWN);

export const selectClusterConnectionStatus = (uuid: string, clusterName: string) => createSelector(selectHasWebsocketConnection(clusterName), selectCtcAvailability(uuid), selectMediaConnectionIsConnected(uuid), selectClusterLocalApplicationIsLoggedIn(clusterName), (ctcWebsocket, { available }, astWebsocket, loggedIn) =>
    ({ http: available, ctcWebsocket: ctcWebsocket, astWebsocket: astWebsocket, loggedIn: loggedIn }));

export const selectIsAlerting = (alertId: string) => createSelector(selectCallAlertsMap, selectCalls, selectMemoizedUserAuthenticationRecord,
    (callAlertMap, calls, user) => user && calls ? calls.some((call) => CallAlertFunctions.matchCall(call, callAlertMap[alertId], user)) : false
);
